import "./common.styles.scss";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useSearchPopover from "./useSearchPopover";

// Results used to render inline (`position: absolute`, z-index 100) directly
// under the input. Inside an AppModal that is a MUI `Dialog scroll="paper"`,
// whose DialogContent is an `overflow: auto` box — so the list was clipped at
// the scroll-box edge, and unusable for any field low in a form.
//
// It is now portalled to <body> and positioned against the input, on the same
// menu tier as AppSelect (1350: above the dialog's 1300, below snackbar 1400).
// `useSearchPopover` owns the anchoring, viewport flip and dismissal — notably
// it treats the portalled panel as "inside" for outside-click purposes, which a
// plain ClickOutside wrapper could not do once the panel left the subtree.
const RESULTS_MAX_HEIGHT = 450;

// Task 24630 - how long to sit on a keystroke before asking the server. Long
// enough that typing a name is one request rather than eight, short enough that
// it still feels live at a terminal.
const ASYNC_DEBOUNCE_MS = 300;

// Change #100544 - other names a customer is known by (Task 24758). Customer
// pickers pass the raw /customer/list or /customer/lookup row as `item`, which
// now carries customerknownname_list, so every customer picker built on this
// searches and shows them without its own change. `option.alsoKnownAs` (a
// string) overrides it for anything that is not a customer row.
const alsoKnownAs = (option) =>
  option?.alsoKnownAs ??
  (option?.item?.customerknownname_list || [])
    .map((n) => n?.Name)
    .filter(Boolean)
    .join(", ");

export const CustomSelect = ({
  placeholder,
  value,
  onClickCallBack,
  options,
  style,
  addNewItem,
  addNewItemCallBack,
  page,
  // Task 24630 - optional server-side mode. Pass `asyncSearch` (a function
  // taking the typed term and resolving to the same option shape as `options`)
  // and the list stops being a client-side filter over everything that was
  // preloaded: nothing is fetched until someone types. Leave it off and this
  // component behaves exactly as it always has.
  asyncSearch,
  asyncMinChars = 2,
  asyncPrompt,
}) => {
  const [anchorWidth, setAnchorWidth] = useState(0);
  const {
    open: showSearchResults,
    close: closeSearchResults,
    coords,
    anchorRef,
    popRef,
    openMenu,
    guardSelect,
  } = useSearchPopover({ width: anchorWidth, maxHeight: RESULTS_MAX_HEIGHT });

  // The panel matches the control's width, so it has to be measured rather than
  // fixed. Feeding it back into the hook re-runs its positioning.
  const openResults = useCallback(
    (e) => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect?.width) setAnchorWidth(rect.width);
      openMenu(e);
    },
    [anchorRef, openMenu]
  );

  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [asyncLoading, setAsyncLoading] = useState(false);
  // Responses can land out of order; only the newest request may paint.
  const asyncSeqRef = useRef(0);
  const [allowedSearchFilters, setAllowedSearchFilters] = useState([
    "FullName",
    "Title",
    "Product_Name",
    "Customer_FullName",
    "Id",
    "AddressLine1",
    "AddressLine2",
    "AddressLine3",
    "AddressLine4",
    "AddressCity",
    "AddressCounty",
    "AddressPostCode",
    "AddressCountry",
    "RegistrationPlate",
    "Chassis",
    "MinistryId",
    "Model",
    "ModelYear",
    "MileageValue",
    "SiteReference",
  ]);
  // Task 24630 - server-side mode. Debounced, and short terms are refused up
  // front rather than asking the server for something that would match half the
  // book. A stale response is dropped by sequence number.
  useEffect(() => {
    if (!asyncSearch) return undefined;

    const term = searchQuery?.trim() || "";
    if (term.length < asyncMinChars) {
      asyncSeqRef.current += 1; // cancel anything in flight
      setAsyncLoading(false);
      setSearchResults([]);
      return undefined;
    }

    const seq = ++asyncSeqRef.current;
    setAsyncLoading(true);
    const timer = setTimeout(() => {
      Promise.resolve()
        .then(() => asyncSearch(term))
        .then((results) => {
          if (seq !== asyncSeqRef.current) return;
          setSearchResults(Array.isArray(results) ? results : []);
          setAsyncLoading(false);
        })
        .catch(() => {
          if (seq !== asyncSeqRef.current) return;
          setSearchResults([]);
          setAsyncLoading(false);
        });
    }, ASYNC_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [asyncSearch, asyncMinChars, searchQuery]);

  useEffect(() => {
    if (asyncSearch) return;
    if (searchQuery) {
      const results = options?.filter((option) => {
        // return allowedSearchFilters?.some((filter) => {
        //   return option?.item[filter]?.toLowerCase()?.includes(searchQuery);
        // });
        return (
          alsoKnownAs(option)
            .toLowerCase()
            .includes(searchQuery?.toLowerCase()) ||
          (option?.firstLine?.label &&
            option?.firstLine?.label
              ?.toLowerCase()
              .includes(searchQuery?.toLowerCase())) ||
          (option?.secondLine?.label &&
            option?.secondLine?.label
              ?.toLowerCase()
              .includes(searchQuery?.toLowerCase())) ||
          (option?.thirdLine?.label &&
            option?.thirdLine?.label
              ?.toLowerCase()
              .includes(searchQuery?.toLowerCase())) ||
          (option?.forthLine?.label &&
            option?.forthLine?.label
              ?.toLowerCase()
              .includes(searchQuery?.toLowerCase()))
        );
      });
      setSearchResults(results);
    } else {
      setSearchResults(options);
    }
  }, [asyncSearch, searchQuery, options]);

  const [selectedOption, setSelectedOption] = useState(null);
  const [tempPlaceHolder, setTempPlaceHolder] = useState(placeholder);
  useEffect(() => {
    setTempPlaceHolder(placeholder);
  }, [placeholder]);

  useEffect(() => {
    if (value) {
      // In async mode `options` is whatever the last search returned, so the
      // selected row may not be in it - fall back to the results actually on
      // screen, and keep the current selection rather than blanking it.
      const option =
        options?.find((option) => option.id === value) ||
        searchResults?.find((option) => option.id === value);
      if (option) setSelectedOption(option);
    }
  }, [value, options, searchResults]);
  const handleOptionSelection = (option) => {
    setSelectedOption(option);
    closeSearchResults();
    setSearchQuery("");
    if (page !== "invoiceCreateEdit") {
      setTempPlaceHolder(option.firstLine.label);
    }

    onClickCallBack(option);
  };
  return (
    <div>
      <div>
        <div className={"d-flex flex-row w-100"} ref={anchorRef}>
          <div className={"w-100"}>
            <input
              autoFocus={false}
              placeholder={tempPlaceHolder}
              className={"form-control custom-select-search-input"}
              type={"text"}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              onFocus={openResults}
            />
          </div>

          <div className={"custom-select-search-section-icon"}>
            <span
              className={"custom-select-search-section-icon-clear"}
              onClick={(e) => {
                if (showSearchResults) closeSearchResults();
                else openResults(e);
              }}
            >
              <i className="fa-solid fa-chevron-down"></i>
            </span>
            <span
              className={"custom-select-search-section-icon-dropdown"}
              onClick={() => {
                setTempPlaceHolder("Select...");
                setSelectedOption(null);
                onClickCallBack(null);
                setSearchQuery("");
              }}
            >
              x
            </span>
          </div>
        </div>
        {showSearchResults &&
          coords &&
          createPortal(
            <div
              ref={popRef}
              className={"custom-select-display-results-main-frame"}
              // Caller `style` first (JobCreateEdit passes maxWidth) so our
              // anchoring wins, but their sizing still applies.
              style={{
                ...style,
                position: "fixed",
                zIndex: 1350,
                left: coords.left,
                top: coords.top,
                bottom: coords.bottom,
                width: anchorWidth || undefined,
                maxHeight: coords.maxHeight,
                overflowY: "auto",
              }}
            >
              <div
                className={"custom-select-display-results-main-frame-wrapper"}
              >
                  {asyncSearch && asyncLoading && (
                    <div className="custom-select-search-results text-muted">
                      <i className="ri-loader-4-line me-1"></i>
                      Searching...
                    </div>
                  )}
                  {asyncSearch &&
                    !asyncLoading &&
                    (searchQuery?.trim()?.length || 0) < asyncMinChars && (
                      <div className="custom-select-search-results text-muted">
                        {asyncPrompt ||
                          `Type at least ${asyncMinChars} characters to search`}
                      </div>
                    )}
                  {asyncSearch &&
                    !asyncLoading &&
                    (searchQuery?.trim()?.length || 0) >= asyncMinChars &&
                    !searchResults?.length && (
                      <div className="custom-select-search-results text-muted">
                        No matches for "{searchQuery.trim()}"
                      </div>
                    )}
                  {searchResults?.map((option, index) => (
                    <div
                      key={option?.id}
                      className={
                        // divide id with moodul and assign even odd as class
                        (index % 2 === 0
                          ? "custom-select-search-results-odd"
                          : "custom-select-search-results-even") +
                        " custom-select-search-results"
                      }
                      onClick={guardSelect(() => {
                        handleOptionSelection(option);
                      })}
                    >
                      <div
                        className={
                          option?.id === selectedOption?.id
                            ? "custom-select-search-results-active"
                            : ""
                        }
                      >
                        <div className={"custom-select-first-line"}>
                          {option?.firstLine?.label}
                        </div>
                        <div className={"custom-select-first-second-line"}>
                          {option?.secondLine?.label}
                        </div>
                        <div>
                          <div className={"custom-select-first-second-line"}>
                            {option?.thirdLine?.label}
                          </div>
                          <div className={"custom-select-first-second-line"}>
                            {option?.forthLine?.label}
                          </div>
                          {alsoKnownAs(option) && (
                            <div className={"custom-select-first-second-line"}>
                              Also known as: {alsoKnownAs(option)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

              <div>
                {addNewItem && (
                  <div
                    className={"my-2 me-2"}
                    onClick={guardSelect(() => {
                      //close the search results
                      closeSearchResults();
                      addNewItemCallBack();
                    })}
                  >
                    <div>
                      <div className={"btn btn-success w-100"}>Add New</div>
                    </div>
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};
